import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Calendar, Loader2 } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { useSearchParams } from "react-router-dom"
import cn from "classnames"
import { useOnboardingConfig } from "@/components/onboarding/OnboardingSteps"

export default function SettingsPage({ profile, session }: { profile: any | null, session: any | null }) {
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const { updateConfig } = useOnboardingConfig();

  const { data: calendarStatus, isLoading: isLoadingStatus, refetch: refetchStatus } = useQuery({
    queryKey: ["calendar-status"],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/me/connect/calendar/status`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      )
      if (!response.ok) {
        throw new Error("Failed to fetch calendar status")
      }
      return response.json()
    },
    enabled: !!session?.access_token,
  })

  useEffect(() => {
    updateConfig({ calendar: calendarStatus?.isConnected })
  }, [calendarStatus, updateConfig])

  useEffect(() => {
    // Check for calendar connection status
    if (searchParams.get("connect") === "calendar") {
      const status = searchParams.get("status")
      if (status === "success") {
        toast({
          title: "Success",
          description: "Google Calendar connected successfully!",
        })
      } else if (status === "error") {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to connect Google Calendar. Please try again.",
        })
      }
    }
  }, [searchParams, toast])

  const handleConnectCalendar = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/me/connect/calendar`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error("Failed to initiate calendar connection")
      }

      const { authUrl } = await response.json()
      
      // Open Google OAuth consent screen in a new window
      const width = 600
      const height = 700
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      
      const authWindow = window.open(
        authUrl,
        "Connect Google Calendar",
        `width=${width},height=${height},left=${left},top=${top}`
      )

      if (!authWindow) {
        throw new Error("Failed to open popup window. Please allow popups and try again.")
      }

      // Create a promise to handle the callback
      const connectionPromise = new Promise<boolean>((resolve, reject) => {
        let timeoutId: NodeJS.Timeout
        let checkInterval: NodeJS.Timeout
        let closeCheckInterval: NodeJS.Timeout
        let connectionReceived = false

        // Listen for postMessage from the callback window
        const messageHandler = (event: MessageEvent) => {
          // Verify origin for security - accept localhost:3000 for development
          const expectedOrigin = import.meta.env.VITE_CC_API_URL?.replace('/v1', '') || 'http://localhost:3000'
          if (event.origin !== expectedOrigin) {
            console.warn("Ignoring message from unknown origin:", event.origin, "expected:", expectedOrigin)
            return
          }

          // Verify the message is from our auth window (more lenient check)
          if (authWindow && !authWindow.closed && event.source !== authWindow) {
            return
          }

          if (event.data?.type === 'CALENDAR_CONNECTED') {
            connectionReceived = true
            cleanup()
            resolve(true)
          } else if (event.data?.type === 'CALENDAR_CONNECTION_ERROR') {
            connectionReceived = true
            cleanup()
            reject(new Error(event.data.error || "Failed to connect calendar"))
          }
        }

        // Fallback: Poll for completion
        checkInterval = setInterval(async () => {
          try {
            const statusResponse = await fetch(
              `${import.meta.env.VITE_CC_API_URL}/v1/me/connect/calendar/status`,
              {
                headers: {
                  Authorization: `Bearer ${session?.access_token}`,
                },
              }
            )
            
            if (statusResponse.ok) {
              const status = await statusResponse.json()
              if (status.isConnected) {
                connectionReceived = true
                cleanup()
                resolve(true)
              }
            }
          } catch (error) {
            console.error("Error checking calendar status:", error)
          }
        }, 2000)

        // Check if window is closed manually
        closeCheckInterval = setInterval(async () => {
          if (authWindow?.closed) {
            
            // If we haven't received a connection response yet, check one more time
            if (!connectionReceived) {
              try {
                const statusResponse = await fetch(
                  `${import.meta.env.VITE_CC_API_URL}/v1/me/connect/calendar/status`,
                  {
                    headers: {
                      Authorization: `Bearer ${session?.access_token}`,
                    },
                  }
                )
                
                if (statusResponse.ok) {
                  const status = await statusResponse.json()
                  if (status.isConnected) {
                    cleanup()
                    resolve(true)
                    return
                  }
                }
              } catch (error) {
                console.error("Error checking final calendar status:", error)
              }
            }
            
            cleanup()
            if (!connectionReceived) {
              reject(new Error("Authentication window was closed"))
            }
          }
        }, 500)

        // Timeout after 5 minutes
        timeoutId = setTimeout(() => {
          cleanup()
          reject(new Error("Connection timed out"))
        }, 300000)

        const cleanup = () => {
          window.removeEventListener('message', messageHandler)
          clearInterval(checkInterval)
          clearInterval(closeCheckInterval)
          clearTimeout(timeoutId)
          if (authWindow && !authWindow.closed) {
            authWindow.close()
          }
        }

        // Add the message listener
        window.addEventListener('message', messageHandler)
      })

      // Wait for the connection to complete
      await connectionPromise
      
      // Refresh status and show success message
      refetchStatus()
      toast({
        title: "Success",
        description: "Google Calendar connected successfully!",
      })

    } catch (error) {
      console.error("Calendar connection error:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to connect Google Calendar. Please try again.",
      })
    }
  }

  const handleDisconnectCalendar = async () => {
    setIsDisconnecting(true)
    try {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/me/connect/calendar`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error("Failed to disconnect calendar")
      }

      refetchStatus()
      toast({
        title: "Success",
        description: "Google Calendar disconnected successfully.",
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to disconnect Google Calendar. Please try again.",
      })
    } finally {
      setIsDisconnecting(false)
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Settings</h1>
      
      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Calendar Integration</CardTitle>
            <CardDescription>
              Connect your Google Calendar to automatically sync your event schedules
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Calendar className={cn(
                    "h-5 w-5",
                    calendarStatus?.isConnected ? "text-green-500" : "text-muted-foreground"
                  )} />
                  <span className="font-medium">
                    Google Calendar
                  </span>
                  {calendarStatus?.isConnected && (
                    <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                      Connected
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {isLoadingStatus ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Checking connection status...
                    </span>
                  ) : calendarStatus?.isConnected ? (
                    <>Connected as <span className="font-medium">{calendarStatus.email}</span></>
                  ) : (
                    "Connect your Google Calendar to sync events"
                  )}
                </p>
                {calendarStatus?.isConnected && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Last synced: {new Date(calendarStatus.lastSync).toLocaleString()}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {calendarStatus?.isConnected && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetchStatus()}
                    disabled={isLoadingStatus}
                    className="gap-2"
                  >
                    {isLoadingStatus ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          fillRule="evenodd"
                          d="M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H3.989a.75.75 0 00-.75.75v4.242a.75.75 0 001.5 0v-2.43l.31.31a7 7 0 0011.712-3.138.75.75 0 00-1.449-.39zm1.23-3.723a.75.75 0 00.219-.53V2.929a.75.75 0 00-1.5 0v2.43l-.31-.31A7 7 0 003.239 8.188a.75.75 0 101.448.389A5.5 5.5 0 0113.89 6.11l.311.31h-2.432a.75.75 0 000 1.5h4.243a.75.75 0 00.53-.219z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    <span className="sr-only">Refresh status</span>
                  </Button>
                )}
                <Button
                  variant={calendarStatus?.isConnected ? "destructive" : "default"}
                  onClick={
                    calendarStatus?.isConnected
                      ? handleDisconnectCalendar
                      : handleConnectCalendar
                  }
                  disabled={isLoadingStatus || isDisconnecting}
                  className="min-w-[100px]"
                >
                  {isDisconnecting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : calendarStatus?.isConnected ? (
                    "Disconnect"
                  ) : (
                    "Connect"
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
